<template>
  <!-- A person at every step: the question goes to the team with the topic it is about, and the answer comes back by
       e-mail or by phone. Opened from the getting started, the account menu and /welcome (composables/useHelpDesk.js). -->
  <UModal v-model:open="open" :title="t('help.title')" :description="t('help.description')" :ui="{ content: 'sm:max-w-lg' }">
    <template #body>
      <div v-if="result === 'sent'" class="help-done" role="status">
        <span class="help-done__icon" aria-hidden="true"><UIcon name="i-heroicons-check" /></span>
        <p class="help-done__title">{{ t("help.sentTitle") }}</p>
        <p class="help-text">
          {{ sent.callback ? t("help.sentCallback", { phone: sent.phone }) : t("help.sentEmail", { email }) }}
          {{ t("help.sentAfter") }}
        </p>
      </div>

      <form v-else id="help-form" class="help-form" novalidate @submit.prevent="submit">
        <UFormField :label="t('help.topic')">
          <USelect v-model="topic" :items="topics" class="w-full" />
        </UFormField>

        <UFormField :label="t('help.question')" :error="messageError || undefined">
          <UTextarea v-model="message" :rows="5" :maxlength="MESSAGE_MAX" autoresize class="w-full" :placeholder="t('help.placeholder')" />
        </UFormField>

        <fieldset class="help-fieldset">
          <legend class="help-legend">{{ t("help.how") }}</legend>
          <div class="help-ways">
            <label v-for="way in WAYS" :key="way.value" class="help-way" :class="{ 'help-way--on': callback === way.callback }">
              <input v-model="callback" class="help-way__input" type="radio" name="help-way" :value="way.callback">
              <UIcon :name="way.icon" class="help-way__icon" aria-hidden="true" />
              <span>{{ t(way.label) }}</span>
            </label>
          </div>
          <p v-if="!callback" class="help-text help-text--small">{{ t("help.answerTo", { email }) }}</p>
          <UFormField v-else :label="t('help.phone')" :error="phoneError || undefined" class="help-phone">
            <UInput v-model="phone" type="tel" autocomplete="tel" placeholder="06 12 34 56 78" class="w-full" />
          </UFormField>
        </fieldset>

        <p class="help-text help-text--small">
          <UIcon name="i-heroicons-shield-check" class="help-text__icon" aria-hidden="true" />
          {{ t("help.noKey") }}
        </p>

        <i18n-t v-if="result === 'unreachable'" keypath="help.unreachable" tag="p" class="help-alert" role="alert" scope="global">
          <template #email><a class="help-link" :href="mailto">{{ CONTACT_EMAIL }}</a></template>
        </i18n-t>
      </form>
    </template>

    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <template v-if="result === 'sent'">
          <UButton color="neutral" @click="open = false">{{ t("help.close") }}</UButton>
        </template>
        <template v-else>
          <UButton color="neutral" variant="outline" :disabled="sending" @click="open = false">{{ t("help.cancel") }}</UButton>
          <UButton type="submit" form="help-form" color="neutral" icon="i-heroicons-paper-airplane" :loading="sending">{{ t("help.send") }}</UButton>
        </template>
      </div>
    </template>
  </UModal>
</template>

<script setup>
import { CONTACT_EMAIL } from "~/composables/useHelpDesk";
import { HELP_GENERAL, helpTopics } from "~/utils/onboarding";

const MESSAGE_MAX = 1000;
const WAYS = [
  { callback: false, label: "help.byEmail", icon: "i-heroicons-envelope" },
  { callback: true, label: "help.byPhone", icon: "i-heroicons-phone" }
];

const { t } = useI18n();
const { $user } = useNuxtApp();
const { state, send } = useHelpDesk();
const { trackHelpRequested } = useTracking();

const email = computed(() => $user?.email ?? t("help.yourEmail"));
const topics = computed(() => helpTopics().map((candidate) => ({ value: candidate.value, label: t(candidate.labelKey) })));

const open = computed({
  get: () => state.value.open,
  set: (value) => { state.value = { ...state.value, open: value }; }
});

const topic = ref(HELP_GENERAL);
const message = ref("");
const callback = ref(false);
const phone = ref($user?.phone ?? "");
const touched = ref(false);
const sending = ref(false);
const result = ref(null); // null | "sent" | "unreachable"
const sent = reactive({ callback: false, phone: "" });

// Each opening takes the topic of the screen that asks; a question being written survives a closing, a sent one does not.
watch(() => state.value.open, (isOpen) => {
  if (!isOpen) return;
  topic.value = state.value.topic;
  callback.value = state.value.callback;
  if (result.value === "sent") {
    message.value = "";
    touched.value = false;
  }
  if (state.value.message && !message.value.trim()) message.value = state.value.message;
  result.value = null;
});

const messageError = computed(() => (touched.value && !message.value.trim() ? t("help.messageError") : ""));
const phoneError = computed(() => (touched.value && callback.value && !/^\+?[0-9][0-9 .()-]{5,24}$/.test(phone.value.trim())
  ? t("help.phoneError") : ""));

const topicLabel = computed(() => topics.value.find((candidate) => candidate.value === topic.value)?.label ?? "");
const mailto = computed(() => `mailto:${ CONTACT_EMAIL }?subject=${ encodeURIComponent(t("help.subject", { topic: topicLabel.value })) }&body=${ encodeURIComponent(message.value) }`);

const submit = async () => {
  touched.value = true;
  if (sending.value || messageError.value || phoneError.value) return;
  sending.value = true;
  const outcome = await send({ topic: topic.value, message: message.value.trim(), callback: callback.value, phone: phone.value.trim() });
  sending.value = false;
  if (outcome === "sent") {
    Object.assign(sent, { callback: callback.value, phone: phone.value.trim() });
    trackHelpRequested({ topic: topic.value, callback: callback.value });
  }
  result.value = outcome === "sent" ? "sent" : "unreachable";
};
</script>

<style lang="scss" scoped>
// Same grammar as the getting started (assets/scss/pages/onboarding.scss): headings #344767, text #67748e, links #4553c9.
.help-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.help-text {
  color: #67748e;
  font-size: 14px;
  line-height: 1.55;

  &--small {
    font-size: 13px;
  }

  &__icon {
    vertical-align: -2px;
    margin-right: 4px;
  }
}

.help-fieldset {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.help-legend {
  margin-bottom: 8px;
  color: #344767;
  font-size: 14px;
  font-weight: 500;
}

.help-ways {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.help-way {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid #dfe4f0;
  border-radius: 999px;
  color: #344767;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;

  &:hover {
    border-color: #4553c9;
  }

  &--on {
    border-color: #4553c9;
    background: #f4f5ff;
    color: #1d2a8f;
    font-weight: 600;
  }

  &:has(.help-way__input:focus-visible) {
    outline: 2px solid #4553c9;
    outline-offset: 2px;
  }

  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &__icon {
    font-size: 16px;
  }
}

.help-alert {
  padding: 12px 16px;
  border-radius: 10px;
  background: #fff4e0;
  color: #7a4a00;
  font-size: 13.5px;
}

.help-link {
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.help-done {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 0 4px;
  text-align: center;

  &__icon {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 999px;
    background: #e6f4ec;
    color: #1b6b47;
    font-size: 22px;
  }

  &__title {
    color: #344767;
    font-size: 16px;
    font-weight: 600;
  }
}

@media (prefers-reduced-motion: reduce) {
  .help-way {
    transition: none;
  }
}
</style>
